import { useCallback, useEffect, useRef, useState } from 'react';
import { validationsAPI } from 'services/swagger-api';

interface UsePendingValidationsCountOptions {
  /** Re-fetch period in milliseconds; no polling when omitted. */
  refreshIntervalMs?: number;
}

interface PendingValidationsCountState {
  /** Pending requests addressed to the account, null until the first answer. */
  count: number | null;
  hasError: boolean;
  refresh: () => void;
}

/**
 * Number of pending validation requests addressed to the current account
 * (dashboard tile and menu badge). Fetched on mount, then every
 * `refreshIntervalMs` when given. Only mount it for sessions holding the
 * validations capability: the endpoint is restricted to validators.
 */
export const usePendingValidationsCount = (
  options: UsePendingValidationsCountOptions = {}
): PendingValidationsCountState => {
  const { refreshIntervalMs } = options;
  const [count, setCount] = useState<number | null>(null);
  const [hasError, setHasError] = useState(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(() => {
    validationsAPI
      .getPendingValidationRequestsCount()
      .then((response) => {
        if (!mountedRef.current) return;
        setCount(response.count);
        setHasError(false);
      })
      .catch(() => {
        if (mountedRef.current) setHasError(true);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (!refreshIntervalMs) return undefined;
    const timer = window.setInterval(refresh, refreshIntervalMs);
    return () => window.clearInterval(timer);
  }, [refresh, refreshIntervalMs]);

  return { count, hasError, refresh };
};
